import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Download,
  Upload,
  Database,
  Shield,
  Users,
  Building2,
  BarChart3,
  CreditCard,
  Settings,
  RefreshCw,
  CheckSquare,
  Square,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileJson,
  Layers,
  ArrowRight,
  Info,
  Clock,
  Hash,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Group metadata ────────────────────────────────────────────────────────────
const GROUP_META = {
  'System Config':    { icon: Settings,   color: 'text-violet-500',  bg: 'bg-violet-500/10',  border: 'border-violet-500/30' },
  'Users & Auth':     { icon: Shield,     color: 'text-blue-500',    bg: 'bg-blue-500/10',    border: 'border-blue-500/30'   },
  'Tenant Structure': { icon: Building2,  color: 'text-orange-500',  bg: 'bg-orange-500/10',  border: 'border-orange-500/30' },
  'CRM Data':         { icon: BarChart3,  color: 'text-green-500',   bg: 'bg-green-500/10',   border: 'border-green-500/30'  },
  'Billing':          { icon: CreditCard, color: 'text-yellow-500',  bg: 'bg-yellow-500/10',  border: 'border-yellow-500/30' },
};

function CountBadge({ count, className = '' }) {
  return (
    <span className={`text-xs font-mono px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 ${className}`}>
      {count?.toLocaleString() ?? '—'}
    </span>
  );
}

// ─── Collection Checkbox Group ─────────────────────────────────────────────────
function CollectionGroup({ groupName, collections, registry, counts, selected, onToggle, onToggleGroup }) {
  const meta = GROUP_META[groupName] || GROUP_META['System Config'];
  const GroupIcon = meta.icon;
  const groupKeys = collections.map(([k]) => k);
  const allChecked = groupKeys.every(k => selected.has(k));
  const someChecked = groupKeys.some(k => selected.has(k));

  return (
    <div className={`rounded-xl border ${meta.border} overflow-hidden`}>
      {/* Group header */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${meta.bg} cursor-pointer select-none`}
        onClick={() => onToggleGroup(groupKeys, !allChecked)}
      >
        <div className="flex items-center gap-2">
          <GroupIcon className={`w-4 h-4 ${meta.color}`} />
          <span className={`font-semibold text-sm ${meta.color}`}>{groupName}</span>
          <Badge variant="secondary" className="text-xs">{collections.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {groupKeys.filter(k => selected.has(k)).length}/{groupKeys.length}
          </span>
          {allChecked
            ? <CheckSquare className={`w-4 h-4 ${meta.color}`} />
            : someChecked
            ? <CheckSquare className="w-4 h-4 text-gray-400" />
            : <Square className="w-4 h-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Collection rows */}
      <div className="divide-y dark:divide-gray-800">
        {collections.map(([key, info]) => (
          <label
            key={key}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.has(key)}
              onChange={() => onToggle(key)}
              className="w-4 h-4 accent-blue-600 rounded"
              data-testid={`backup-checkbox-${key}`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{info.label}</span>
                {key === 'users' && (
                  <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400">
                    sensitive
                  </Badge>
                )}
              </div>
              <p className="text-xs text-gray-400 truncate">{info.description}</p>
            </div>
            <CountBadge count={counts?.[key]} />
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
const BackupRestorePanel = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  // Preview data from server
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  // Backup selections
  const [backupSelected, setBackupSelected] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);

  // Restore state
  const [restoreFile, setRestoreFile] = useState(null);     // parsed JSON object
  const [restoreFileName, setRestoreFileName] = useState('');
  const [restoreSelected, setRestoreSelected] = useState(new Set());
  const [restoreMode, setRestoreMode] = useState('merge');  // 'overwrite' | 'merge'
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAck, setConfirmAck] = useState(false);
  const [lastRestoreResult, setLastRestoreResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Load preview on mount
  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API_URL}/api/platform/backup/preview`, { headers });
      setPreview(res.data);
      // Default: all collections selected for backup
      setBackupSelected(new Set(Object.keys(res.data.registry)));
    } catch (err) {
      toast.error('Failed to load backup preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const toggleCollection = (set, setFn, key) => {
    setFn(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleGroup = (set, setFn, keys, toChecked) => {
    setFn(prev => {
      const next = new Set(prev);
      keys.forEach(k => toChecked ? next.add(k) : next.delete(k));
      return next;
    });
  };

  const selectPreset = (preset) => {
    if (!preview) return;
    const all = new Set(Object.keys(preview.registry));
    if (preset === 'all')    return setBackupSelected(all);
    if (preset === 'none')   return setBackupSelected(new Set());
    if (preset === 'config') {
      const keys = Object.entries(preview.registry)
        .filter(([, v]) => v.group === 'System Config')
        .map(([k]) => k);
      return setBackupSelected(new Set(keys));
    }
    if (preset === 'users') {
      const keys = Object.entries(preview.registry)
        .filter(([, v]) => ['Users & Auth', 'Tenant Structure'].includes(v.group))
        .map(([k]) => k);
      return setBackupSelected(new Set(keys));
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExport = async () => {
    if (backupSelected.size === 0) {
      toast.error('Select at least one collection to back up.');
      return;
    }
    setExportLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/platform/backup/export`,
        { collections: Array.from(backupSelected) },
        { headers }
      );
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `fbp-backup-${ts}.fbp.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded — ${res.data.meta.total_records} records across ${backupSelected.size} collections`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // ── File Loading ──────────────────────────────────────────────────────────

  const loadFile = (file) => {
    if (!file) return;
    setRestoreFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!parsed.meta || !parsed.data) throw new Error('Not a valid .fbp.json backup');
        setRestoreFile(parsed);
        // Default restore selection = same as what's in the backup
        setRestoreSelected(new Set(parsed.meta.collections_included));
        setLastRestoreResult(null);
        toast.success(`Loaded backup from ${parsed.meta.created_at?.slice(0, 10) || 'unknown date'}`);
      } catch (err) {
        toast.error('Invalid backup file: ' + err.message);
        setRestoreFile(null);
        setRestoreFileName('');
      }
    };
    reader.readAsText(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) loadFile(file);
  };

  // ── Restore ────────────────────────────────────────────────────────────────

  const handleRestoreConfirm = async () => {
    setRestoreLoading(true);
    setConfirmOpen(false);
    setConfirmAck(false);
    try {
      const res = await axios.post(
        `${API_URL}/api/platform/backup/restore`,
        {
          backup: restoreFile,
          selected_collections: Array.from(restoreSelected),
          mode: restoreMode,
        },
        { headers }
      );
      setLastRestoreResult(res.data);
      if (res.data.success) {
        toast.success('Restore completed successfully');
        fetchPreview(); // refresh counts
      } else {
        toast.error(`Restore finished with ${res.data.errors.length} error(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreLoading(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const groupedRegistry = preview
    ? preview.group_order.map(group => ({
        group,
        items: Object.entries(preview.registry).filter(([, v]) => v.group === group),
      }))
    : [];

  const selectedBackupCount = preview
    ? Array.from(backupSelected).reduce((s, k) => s + (preview.counts[k] || 0), 0)
    : 0;

  // ── Loading state ──────────────────────────────────────────────────────────

  if (loadingPreview) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading collections…</span>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="backup-restore-panel">

      {/* ═══════════════════════════════════════════════════════════════════════
           BACKUP SECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-blue-600/10 flex items-center justify-center">
            <Download className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create Backup</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Choose which collections to include and download a <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">.fbp.json</code> snapshot.
            </p>
          </div>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={fetchPreview} data-testid="refresh-preview-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Quick-select presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-xs text-gray-400 self-center mr-1">Quick select:</span>
          {[
            { id: 'all',    label: 'Everything' },
            { id: 'config', label: 'Config Only' },
            { id: 'users',  label: 'Users & Tenants' },
            { id: 'none',   label: 'Clear All' },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => selectPreset(p.id)}
              className="text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Collection groups grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          {groupedRegistry.map(({ group, items }) => (
            <CollectionGroup
              key={group}
              groupName={group}
              collections={items}
              registry={preview.registry}
              counts={preview.counts}
              selected={backupSelected}
              onToggle={(key) => toggleCollection(backupSelected, setBackupSelected, key)}
              onToggleGroup={(keys, val) => toggleGroup(backupSelected, setBackupSelected, keys, val)}
            />
          ))}
        </div>

        {/* Export bar */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-xl px-5 py-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <FileJson className="w-8 h-8 text-blue-500" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white text-sm">
                {backupSelected.size} collection{backupSelected.size !== 1 ? 's' : ''} selected
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                ~{selectedBackupCount.toLocaleString()} total records · downloads as <code className="text-xs">.fbp.json</code>
              </p>
            </div>
          </div>
          <Button
            onClick={handleExport}
            disabled={exportLoading || backupSelected.size === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="download-backup-btn"
          >
            {exportLoading
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
              : <><Download className="w-4 h-4 mr-2" /> Download Backup</>
            }
          </Button>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Restore</span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
           RESTORE SECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <Upload className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Restore from Backup</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Upload a <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">.fbp.json</code> file and selectively restore collections.
            </p>
          </div>
        </div>

        {/* Warning banner */}
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3 mb-5">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800 dark:text-amber-300">Restore modifies live data</p>
            <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
              <strong>Overwrite</strong> permanently deletes existing records before inserting. <strong>Merge</strong> is non-destructive but will overwrite matching records. Create a fresh backup before proceeding.
            </p>
          </div>
        </div>

        {/* File drop zone */}
        {!restoreFile ? (
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer
              ${dragOver
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
              }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="restore-drop-zone"
          >
            <Database className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="font-semibold text-gray-700 dark:text-gray-300">Drop your .fbp.json backup here</p>
            <p className="text-sm text-gray-400 mt-1">or click to browse files</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.fbp.json"
              className="hidden"
              onChange={(e) => loadFile(e.target.files[0])}
              data-testid="restore-file-input"
            />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Backup metadata card */}
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileJson className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{restoreFileName}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {restoreFile.meta.created_at ? new Date(restoreFile.meta.created_at).toLocaleString() : '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Hash className="w-3 h-3" />
                        {restoreFile.meta.total_records?.toLocaleString()} records
                      </span>
                      <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {restoreFile.meta.collections_included?.length} collections
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setRestoreFile(null); setRestoreFileName(''); setRestoreSelected(new Set()); setLastRestoreResult(null); }}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  data-testid="clear-restore-file-btn"
                >
                  Change file
                </button>
              </div>

              {/* Record count comparison */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {restoreFile.meta.collections_included?.map(name => (
                  <div key={name} className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border dark:border-gray-700">
                    <span className="text-xs text-gray-600 dark:text-gray-300 truncate mr-2">
                      {preview?.registry[name]?.label || name}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs font-mono text-blue-600 dark:text-blue-400">
                        {restoreFile.meta.record_counts?.[name] ?? 0}
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-xs font-mono text-gray-500">
                        {preview?.counts?.[name] ?? 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                <Info className="w-3 h-3 inline mr-1" />
                Numbers show: <span className="text-blue-500">backup</span> → <span className="text-gray-400">current DB</span> record counts.
              </p>
            </div>

            {/* Collection selection for restore */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Select collections to restore</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRestoreSelected(new Set(restoreFile.meta.collections_included))}
                    className="text-xs text-blue-600 hover:underline"
                  >All</button>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <button
                    onClick={() => setRestoreSelected(new Set())}
                    className="text-xs text-gray-500 hover:underline"
                  >None</button>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {preview?.group_order.map(group => {
                  const items = restoreFile.meta.collections_included
                    .filter(k => preview.registry[k]?.group === group)
                    .map(k => [k, preview.registry[k]]);
                  if (!items.length) return null;
                  return (
                    <CollectionGroup
                      key={group}
                      groupName={group}
                      collections={items}
                      registry={preview.registry}
                      counts={restoreFile.meta.record_counts}
                      selected={restoreSelected}
                      onToggle={(key) => toggleCollection(restoreSelected, setRestoreSelected, key)}
                      onToggleGroup={(keys, val) => toggleGroup(restoreSelected, setRestoreSelected, keys, val)}
                    />
                  );
                })}
              </div>
            </div>

            {/* Mode selector */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Restore mode</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    id: 'merge',
                    icon: Layers,
                    label: 'Merge',
                    badge: 'Safe',
                    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
                    desc: 'Upserts each record by ID. Existing records not in the backup are untouched. Matching records are overwritten.',
                    iconColor: 'text-green-500',
                    borderActive: 'border-green-500',
                  },
                  {
                    id: 'overwrite',
                    icon: AlertTriangle,
                    label: 'Complete Overwrite',
                    badge: 'Destructive',
                    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
                    desc: 'Deletes ALL existing documents in each selected collection, then inserts backup data. Cannot be undone.',
                    iconColor: 'text-red-500',
                    borderActive: 'border-red-500',
                  },
                ].map(m => (
                  <label
                    key={m.id}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all
                      ${restoreMode === m.id
                        ? m.borderActive + ' bg-gray-50 dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    data-testid={`restore-mode-${m.id}`}
                  >
                    <input
                      type="radio"
                      name="restore-mode"
                      value={m.id}
                      checked={restoreMode === m.id}
                      onChange={() => setRestoreMode(m.id)}
                      className="mt-1 accent-blue-600"
                    />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <m.icon className={`w-4 h-4 ${m.iconColor}`} />
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">{m.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.badgeClass}`}>{m.badge}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{m.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Restore result */}
            {lastRestoreResult && (
              <div className={`rounded-xl border p-4 ${lastRestoreResult.success
                ? 'border-green-500/30 bg-green-50 dark:bg-green-900/20'
                : 'border-red-500/30 bg-red-50 dark:bg-red-900/20'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {lastRestoreResult.success
                    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : <XCircle className="w-5 h-5 text-red-600" />
                  }
                  <span className="font-semibold text-sm">
                    {lastRestoreResult.success ? 'Restore completed' : `Restore finished with ${lastRestoreResult.errors.length} error(s)`}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {Object.entries(lastRestoreResult.results || {}).map(([col, r]) => (
                    <div key={col} className="bg-white dark:bg-gray-900 rounded-lg px-3 py-2 border dark:border-gray-700 text-xs">
                      <p className="font-medium text-gray-800 dark:text-gray-200 mb-1 truncate">
                        {preview?.registry[col]?.label || col}
                      </p>
                      {r.mode === 'overwrite'
                        ? <p className="text-gray-500">Deleted {r.deleted}, inserted {r.inserted}</p>
                        : <p className="text-gray-500">+{r.inserted} new, ~{r.updated} updated</p>
                      }
                    </div>
                  ))}
                </div>
                {lastRestoreResult.errors?.length > 0 && (
                  <div className="space-y-1">
                    {lastRestoreResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                        <XCircle className="w-3 h-3 mt-0.5 shrink-0" />{e}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Restore action bar */}
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-xl px-5 py-4 border border-gray-200 dark:border-gray-700">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">
                  {restoreSelected.size} collection{restoreSelected.size !== 1 ? 's' : ''} · {restoreMode === 'overwrite' ? 'Complete Overwrite' : 'Merge'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  ~{Array.from(restoreSelected).reduce((s, k) => s + (restoreFile.meta.record_counts?.[k] || 0), 0).toLocaleString()} records will be processed
                </p>
              </div>
              <Button
                onClick={() => { setConfirmAck(false); setConfirmOpen(true); }}
                disabled={restoreLoading || restoreSelected.size === 0}
                className={restoreMode === 'overwrite'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
                }
                data-testid="start-restore-btn"
              >
                {restoreLoading
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Restoring…</>
                  : <><Upload className="w-4 h-4 mr-2" />Restore Now</>
                }
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirmation Dialog ─────────────────────────────────────────────── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${restoreMode === 'overwrite' ? 'text-red-500' : 'text-amber-500'}`} />
              Confirm Restore
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className={`rounded-lg p-4 text-sm ${restoreMode === 'overwrite'
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
            }`}>
              <p className={`font-semibold mb-1 ${restoreMode === 'overwrite' ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'}`}>
                {restoreMode === 'overwrite' ? 'Complete Overwrite — This CANNOT be undone' : 'Merge Restore'}
              </p>
              <p className={`text-xs ${restoreMode === 'overwrite' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {restoreMode === 'overwrite'
                  ? 'All documents in the selected collections will be permanently deleted before restoring backup data.'
                  : 'Records matching existing IDs will be overwritten. New records from the backup will be inserted. Existing records not in the backup will remain unchanged.'
                }
              </p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Collections to restore:</p>
              <div className="flex flex-wrap gap-2">
                {Array.from(restoreSelected).map(k => (
                  <Badge key={k} variant="secondary" className="text-xs">
                    {preview?.registry[k]?.label || k}
                  </Badge>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmAck}
                onChange={(e) => setConfirmAck(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-blue-600"
                data-testid="restore-confirm-ack"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I understand this will modify live data. I have taken a fresh backup if needed.
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRestoreConfirm}
              disabled={!confirmAck}
              className={restoreMode === 'overwrite' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}
              data-testid="confirm-restore-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {restoreMode === 'overwrite' ? 'Yes, Overwrite Everything' : 'Yes, Merge Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default BackupRestorePanel;
